// src/pages/PricingManagement.js
import React, { useEffect, useState } from 'react';
import api from '../services/api';

const ENTITY_LABELS = {
  VEHICLE:     '🚗 Vehicle',
  FAMILY:      '👨‍👩‍👧 Family',
  INSTITUTION: '🏫 Institution',
};

const TYPE_LABELS = {
  DIGITAL:  '📱 Digital',
  PHYSICAL: '📦 Physical',
};

const MATERIAL_LABELS = {
  null:  '—',
  PAPER: 'Paper (Laminated)',
  VINYL: 'Vinyl Sticker',
};

export default function PricingManagement() {
  const [pricing,   setPricing]   = useState([]);
  const [validity,  setValidity]  = useState([]);
  const [gst,       setGst]       = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('pricing');
  const [saving,    setSaving]    = useState(false);
  const [message,   setMessage]   = useState(null);

  // ── Pricing edit state ────────────────────────────────────
  // Each row can edit price AND renewalPrice independently
  const [editingPrice, setEditingPrice]           = useState(null); // row id being edited
  const [editPriceValue, setEditPriceValue]       = useState('');
  const [editRenewalValue, setEditRenewalValue]   = useState('');

  // ── Validity edit state ───────────────────────────────────
  const [editingValidity, setEditingValidity] = useState(null);
  const [validityForm, setValidityForm]       = useState({});

  // ── GST edit state ────────────────────────────────────────
  const [editingGst, setEditingGst] = useState(null);
  const [gstValue, setGstValue]     = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      api.get('/api/admin/pricing'),
      api.get('/api/admin/pricing/validity'),
      api.get('/api/admin/pricing/gst'),
    ]).then(([p, v, g]) => {
      setPricing(p.data?.data || []);
      setValidity(v.data?.data || []);
      setGst(g.data?.data || []);
    }).catch(console.error)
      .finally(() => setLoading(false));
  };

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3500);
  };

  // ── Open pricing row for editing ──────────────────────────
  const openPriceEdit = (item) => {
    setEditingPrice(item.id);
    setEditPriceValue(item.price ?? 0);
    setEditRenewalValue(item.renewalPrice ?? 0);
  };

  // ── Save generation price + renewal price together ────────
  // Both live in qr_pricing_config — single PUT call handles both
  const handlePriceUpdate = async (item) => {
    setSaving(true);
    try {
      await api.put(`/api/admin/pricing/${item.id}`, {
        price:        parseFloat(editPriceValue),
        renewalPrice: parseFloat(editRenewalValue),
        active:       item.active,
      });
      showMsg('success', 'Prices updated successfully');
      setEditingPrice(null);
      loadAll();
    } catch (e) {
      showMsg('error', 'Failed to update prices');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await api.put(`/api/admin/pricing/${item.id}`, {
        price:        item.price,
        renewalPrice: item.renewalPrice,
        active:       !item.active,
      });
      showMsg('success', `${item.active ? 'Disabled' : 'Enabled'} successfully`);
      loadAll();
    } catch (e) {
      showMsg('error', 'Failed to update');
    }
  };

  // ── Open validity row for editing ─────────────────────────
  // NOTE: renewalPrice is NOT here — it is in qr_pricing_config
  const openValidityEdit = (item) => {
    setEditingValidity(item.id);
    setValidityForm({
      validityDays:       item.validityDays,
      gracePeriodDays:    item.gracePeriodDays,
      reminderDaysBefore: item.reminderDaysBefore,
    });
  };

  const handleValidityUpdate = async () => {
    setSaving(true);
    try {
      await api.put(`/api/admin/pricing/validity/${editingValidity}`, validityForm);
      showMsg('success', 'Validity config updated');
      setEditingValidity(null);
      loadAll();
    } catch (e) {
      showMsg('error', 'Failed to update validity');
    } finally {
      setSaving(false);
    }
  };

  // ── GST update ────────────────────────────────────────────
  const handleGstUpdate = async () => {
    setSaving(true);
    try {
      await api.put(`/api/admin/pricing/gst/${editingGst}`, { configValue: gstValue });
      showMsg('success', 'GST config updated');
      setEditingGst(null);
      loadAll();
    } catch (e) {
      showMsg('error', 'Failed to update GST config');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="loading"><div className="spinner" />Loading pricing config...</div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Pricing & Validity Management</h1>
          <p>Configure QR code generation prices, renewal prices, validity and GST settings</p>
        </div>
        <button className="btn btn-secondary" onClick={loadAll}>🔄 Refresh</button>
      </div>

      {message && (
        <div className={`alert-banner ${message.type}`}>{message.text}</div>
      )}

      {/* ── Tabs ───────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #e8e8e8' }}>
        {[
          { key: 'pricing',  label: '💰 QR Pricing' },
          { key: 'validity', label: '📅 Validity Config' },
          { key: 'gst',      label: '🧾 GST Config' },
        ].map(tab => (
          <button key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: activeTab === tab.key ? 700 : 400,
              color: activeTab === tab.key ? '#0066cc' : '#666',
              borderBottom: activeTab === tab.key ? '2px solid #0066cc' : '2px solid transparent',
              marginBottom: -2,
              fontSize: 14,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── QR Pricing Tab ─────────────────────────────────── */}
      {activeTab === 'pricing' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">QR Code Pricing Configuration</span>
            <span style={{ fontSize: 12, color: '#666' }}>
              Set price to 0.00 for FREE. Renewal price is charged when renewing an expired QR.
            </span>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Entity Type</th>
                  <th>QR Type</th>
                  <th>Material</th>
                  <th>Display Name</th>
                  <th>Generation Price</th>
                  <th>Renewal Price</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pricing.map(item => (
                  <tr key={item.id}>
                    <td><strong>{ENTITY_LABELS[item.entityType] || item.entityType}</strong></td>
                    <td>{TYPE_LABELS[item.qrType] || item.qrType}</td>
                    <td style={{ color: '#666', fontSize: 12 }}>
                      {MATERIAL_LABELS[item.materialType] || item.materialType || '—'}
                    </td>
                    <td style={{ fontSize: 12, color: '#444' }}>{item.displayName}</td>

                    {/* ── Generation Price ─────────────────── */}
                    <td>
                      {editingPrice === item.id ? (
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <span style={{ color: '#666' }}>₹</span>
                          <input
                            type="number" min="0" step="0.01"
                            value={editPriceValue}
                            onChange={e => setEditPriceValue(e.target.value)}
                            style={{ width: 80, padding: '4px 8px', border: '1px solid #0066cc', borderRadius: 4 }}
                          />
                        </div>
                      ) : (
                        <strong style={{ color: item.price == 0 ? '#137333' : '#0066cc' }}>
                          {item.price == 0 ? '🆓 FREE' : `₹${item.price}`}
                        </strong>
                      )}
                    </td>

                    {/* ── Renewal Price ─────────────────────── */}
                    <td>
                      {editingPrice === item.id ? (
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <span style={{ color: '#666' }}>₹</span>
                          <input
                            type="number" min="0" step="0.01"
                            value={editRenewalValue}
                            onChange={e => setEditRenewalValue(e.target.value)}
                            style={{ width: 80, padding: '4px 8px', border: '1px solid #e67700', borderRadius: 4 }}
                          />
                        </div>
                      ) : (
                        <strong style={{ color: item.renewalPrice == 0 ? '#137333' : '#e67700' }}>
                          {item.renewalPrice == 0 ? '🆓 FREE' : `₹${item.renewalPrice}`}
                        </strong>
                      )}
                    </td>

                    <td>
                      <span className={`badge ${item.active ? 'badge-success' : 'badge-danger'}`}>
                        {item.active ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {editingPrice === item.id ? (
                          <>
                            <button className="btn btn-success btn-sm"
                              onClick={() => handlePriceUpdate(item)} disabled={saving}>
                              {saving ? '...' : '✓ Save'}
                            </button>
                            <button className="btn btn-secondary btn-sm"
                              onClick={() => setEditingPrice(null)}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="btn btn-secondary btn-sm"
                              onClick={() => openPriceEdit(item)}>
                              ✏️ Edit
                            </button>
                            <button
                              className={`btn btn-sm ${item.active ? 'btn-danger' : 'btn-success'}`}
                              onClick={() => handleToggleActive(item)}>
                              {item.active ? 'Disable' : 'Enable'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0',
              display: 'flex', gap: 24, fontSize: 12, color: '#666' }}>
            <span>
              <strong style={{ color: '#0066cc' }}>Generation Price</strong> — charged when QR is first created
            </span>
            <span>
              <strong style={{ color: '#e67700' }}>Renewal Price</strong> — charged when renewing an expired QR token
            </span>
            <span>
              Renewal is always <strong>DIGITAL</strong> (renews the token, not the physical sticker)
            </span>
          </div>
        </div>
      )}

      {/* ── Validity Config Tab ───────────────────────────── */}
      {activeTab === 'validity' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">QR Code Validity Configuration</span>
            <span style={{ fontSize: 12, color: '#666' }}>
              Set validity_days to 0 for LIFETIME (never expires). Renewal prices are set in the Pricing tab.
            </span>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Entity Type</th>
                  <th>Validity (days)</th>
                  <th>Grace Period (days)</th>
                  <th>Reminder Before (days)</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {validity.map(item => (
                  <React.Fragment key={item.id}>
                    <tr>
                      <td><strong>{ENTITY_LABELS[item.entityType] || item.entityType}</strong></td>
                      <td>
                        <span className="badge badge-info">
                          {item.validityDays === 0 ? '♾️ Lifetime' : `${item.validityDays} days`}
                        </span>
                      </td>
                      <td>{item.gracePeriodDays} days</td>
                      <td>{item.reminderDaysBefore} days before expiry</td>
                      <td>
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => openValidityEdit(item)}>
                          ✏️ Edit
                        </button>
                      </td>
                    </tr>

                    {editingValidity === item.id && (
                      <tr>
                        <td colSpan={5} style={{ background: '#f8f9fa', padding: 20 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                              <label>Validity Days (0 = Lifetime)</label>
                              <input type="number" className="form-control" min="0"
                                value={validityForm.validityDays}
                                onChange={e => setValidityForm(f => ({ ...f, validityDays: parseInt(e.target.value) }))}
                              />
                            </div>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                              <label>Grace Period Days</label>
                              <input type="number" className="form-control" min="0"
                                value={validityForm.gracePeriodDays}
                                onChange={e => setValidityForm(f => ({ ...f, gracePeriodDays: parseInt(e.target.value) }))}
                              />
                            </div>
                            <div className="form-group" style={{ marginBottom: 0 }}>
                              <label>Reminder Days Before Expiry</label>
                              <input type="number" className="form-control" min="0"
                                value={validityForm.reminderDaysBefore}
                                onChange={e => setValidityForm(f => ({ ...f, reminderDaysBefore: parseInt(e.target.value) }))}
                              />
                            </div>
                          </div>
                          <div style={{ padding: '10px 12px', background: '#fff8e1', borderRadius: 6,
                              border: '1px solid #ffe082', fontSize: 12, color: '#795548', marginBottom: 12 }}>
                            💡 <strong>Renewal prices</strong> are configured in the <strong>QR Pricing tab</strong> —
                            find the DIGITAL row for this entity type and edit its Renewal Price column.
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button className="btn btn-success"
                              onClick={handleValidityUpdate} disabled={saving}>
                              {saving ? 'Saving...' : '✓ Save Changes'}
                            </button>
                            <button className="btn btn-secondary"
                              onClick={() => setEditingValidity(null)}>
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── GST Config Tab ───────────────────────────────── */}
      {activeTab === 'gst' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">GST & Invoice Configuration</span>
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Config Key</th>
                  <th>Current Value</th>
                  <th>Description</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {gst.map(item => (
                  <tr key={item.id}>
                    <td><code style={{ fontSize: 12 }}>{item.configKey}</code></td>
                    <td>
                      {editingGst === item.id ? (
                        <input
                          type="text"
                          value={gstValue}
                          onChange={e => setGstValue(e.target.value)}
                          style={{ padding: '4px 8px', border: '1px solid #0066cc', borderRadius: 4, width: 200 }}
                        />
                      ) : (
                        <strong>{item.configValue}</strong>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: '#666' }}>{item.description}</td>
                    <td>
                      {editingGst === item.id ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-success btn-sm"
                            onClick={handleGstUpdate} disabled={saving}>
                            {saving ? '...' : '✓ Save'}
                          </button>
                          <button className="btn btn-secondary btn-sm"
                            onClick={() => setEditingGst(null)}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button className="btn btn-secondary btn-sm"
                          onClick={() => { setEditingGst(item.id); setGstValue(item.configValue); }}>
                          ✏️ Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
